// FILE: src/components/TimeNotices.jsx
// The two time-engine dialogs (plan WP-3), rendered once by SessionGuard so
// they appear on every page, including over the lesson:
//   BreakDialog     — 2 hours of learning: a 15-minute break with a countdown
//                     (MTO 17.2, D4). The server decides when it's over.
//   DailyCapDialog  — 5 hours today: continue tomorrow, Ontario time (17.1).
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Typography
} from "@mui/material";
import CoffeeOutlinedIcon from "@mui/icons-material/CoffeeOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { breakStarted, dismissNotice, selectBreakDeadline } from "../features/timeSlice";
import { startBreak, errorBody } from "../services/learningSession";
import { formatCountdown } from "../utils/timeFormat";
import IconDialogTitle from "./student/IconDialogTitle";

// Both dialogs: the dashboard link is the keyboard way out (README rule 11);
// the break can't be skipped, so Escape doesn't close that one.
const ACTIONS_SX = { px: 3, pb: 2.5, flexWrap: "wrap", gap: 1, "& > :not(style) ~ :not(style)": { ml: 0 } };

function useNow(active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

export function BreakDialog({ onCountdownEnd }) {
  const dispatch = useDispatch();
  const open = useSelector((s) => s.time.notice === "break");
  const onBreak = useSelector((s) => s.time.onBreak);
  const deadline = useSelector(selectBreakDeadline);
  const now = useNow(open);
  const [error, setError] = useState(null);
  const remainingMs = deadline ? Math.max(0, deadline - now) : 0;
  const done = open && deadline !== null && remainingMs === 0;

  useEffect(() => {
    if (done) onCountdownEnd?.();
  }, [done, onCountdownEnd]);

  const begin = async () => {
    setError(null);
    try {
      dispatch(breakStarted(await startBreak()));
    } catch (err) {
      setError(errorBody(err)?.code || "NETWORK");
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" aria-labelledby="break-title" aria-describedby="break-description">
      <IconDialogTitle id="break-title" icon={CoffeeOutlinedIcon} tone="warning">
        Time for a break
      </IconDialogTitle>
      <DialogContent>
        <DialogContentText id="break-description" sx={{ color: "text.primary" }}>
          You've been learning for 2 hours. Ontario rules require a break of at least 15 minutes before you continue.
          Break time doesn't count toward your course hours.
        </DialogContentText>
        <Box sx={{ mt: 2.5, py: 2.5, px: 2, borderRadius: 3, bgcolor: "zun.warningTint", textAlign: "center" }}>
          <Typography
            component="p"
            sx={{ fontSize: "2.5rem", fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}
            role="timer"
            aria-live="off"
            data-testid="break-countdown"
          >
            {formatCountdown(remainingMs)}
          </Typography>
          <Typography sx={{ mt: 0.5 }}>{done ? "Checking that your break is complete…" : "until you can continue"}</Typography>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            We couldn't start your break. Please try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={ACTIONS_SX}>
        <Button component={RouterLink} to="/student" onClick={() => dispatch(dismissNotice())}>
          Go to my dashboard
        </Button>
        {!onBreak && (
          <Button variant="contained" onClick={begin} autoFocus>
            Start my break now
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function DailyCapDialog() {
  const dispatch = useDispatch();
  const open = useSelector((s) => s.time.notice === "cap");
  return (
    <Dialog
      open={open}
      onClose={() => dispatch(dismissNotice())}
      fullWidth
      maxWidth="sm"
      aria-labelledby="cap-title"
      aria-describedby="cap-description"
    >
      <IconDialogTitle id="cap-title" icon={HourglassBottomOutlinedIcon} tone="info">
        Daily limit reached
      </IconDialogTitle>
      <DialogContent>
        <DialogContentText id="cap-description" sx={{ color: "text.primary" }}>
          You've reached today's limit of 5 hours of online lessons. Ontario rules don't allow more in one day. You can
          continue tomorrow, after midnight Ontario time. Your progress is saved.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={ACTIONS_SX}>
        <Button component={RouterLink} to="/student" onClick={() => dispatch(dismissNotice())} variant="contained" autoFocus>
          Go to my dashboard
        </Button>
      </DialogActions>
    </Dialog>
  );
}
